import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { LuCheck, LuShieldCheck } from "react-icons/lu";
import { signInWithEmailAndPassword } from "firebase/auth";
import { doc, getDoc, getDocs, query, collection, where } from "firebase/firestore";
import { auth, db } from "../utils/firebase";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";

const labelCls = "block text-xs font-bold text-[var(--color-muted-foreground)] uppercase tracking-wider mb-1.5";

function Login() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword]     = useState("");
  const [loading, setLoading]       = useState(false);
  const navigate  = useNavigate();
  const location  = useLocation();
  const from      = location.state?.from?.pathname || "/";

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let email = identifier;
      if (!identifier.includes("@")) {
        const snap = await getDocs(query(collection(db, "users"), where("username", "==", identifier)));
        if (snap.empty) { toast.error("Username not found"); setLoading(false); return; }
        email = snap.docs[0].data().email;
      }
      const cred    = await signInWithEmailAndPassword(auth, email, password);
      const isAdmin = (await getDoc(doc(db, "adminUsers", cred.user.uid))).exists();
      if (!cred.user.emailVerified && !isAdmin) {
        toast.error("Please verify your email before logging in.");
        await auth.signOut(); setLoading(false); return;
      }
      if (!isAdmin) {
        const userSnap = await getDoc(doc(db, "users", cred.user.uid));
        const status   = userSnap.exists() ? userSnap.data().status : null;
        if (status !== "approved") {
          const msg = status === "rejected"
            ? "Your wholesale application has been declined. Please contact us for more information."
            : "Your account is pending admin approval. You'll be notified once your application is reviewed.";
          toast.error(msg, { autoClose: 6000 });
          await auth.signOut(); setLoading(false); return;
        }
      }
      toast.success("Login successful!");
      navigate(from, { replace: true });
    } catch {
      toast.error("Invalid login credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex">
      <Helmet>
        <title>Sign In | Khoja Distributions Wholesale Portal</title>
        <meta name="description" content="Sign in to your Khoja Distributions wholesale account to access bulk pricing, track orders, and manage your business account." />
        <link rel="canonical" href="https://www.khojadistributions.com/login" />
        <meta name="robots" content="noindex" />
      </Helmet>

      {/* Left — Enterprise branding panel */}
      <div className="hidden lg:flex flex-col justify-between w-[45%] bg-brand-navy p-14">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="bg-white rounded-lg p-1.5 flex-shrink-0">
            <img src="/images/croppedLogo.png" alt="Khoja Distributions" className="h-9 w-auto" />
          </div>
          <div className="flex flex-col leading-none">
            <span className="text-white font-black text-[16px] tracking-tight" style={{ fontFamily: "Lexend, sans-serif" }}>Khoja</span>
            <span className="text-brand-amber font-bold text-[9px] tracking-[0.2em] uppercase mt-0.5" style={{ fontFamily: "Lexend, sans-serif" }}>Distributions</span>
          </div>
        </Link>
        <div>
          <span className="inline-flex items-center gap-2 bg-brand-amber/15 border border-brand-amber/25 text-brand-amber text-xs font-bold tracking-widest uppercase px-3 py-1 rounded-full mb-6">
            <LuShieldCheck size={12} />
            B2B Wholesale Portal
          </span>
          <h1 className="text-4xl font-black text-white leading-tight tracking-tight mb-4">
            Welcome back to<br />Khoja Distributions
          </h1>
          <p className="text-slate-400 text-base leading-relaxed mb-10">
            Sign in to access exclusive wholesale pricing, track your orders, and manage your business account.
          </p>
          <div className="space-y-3">
            {[
              "Access bulk pricing & tier discounts",
              "Track and manage wholesale orders",
              "Save business shipping addresses",
              "DFW local delivery — 24–48 hrs",
            ].map(item => (
              <div key={item} className="flex items-center gap-3 text-sm text-slate-300">
                <div className="w-5 h-5 rounded-full bg-brand-amber/15 border border-brand-amber/30 flex items-center justify-center flex-shrink-0">
                  <LuCheck size={12} className="text-brand-amber" strokeWidth={3} />
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
        <p className="text-slate-600 text-xs">
          © {new Date().getFullYear()} Khoja Distributions · DFW, Texas
        </p>
      </div>

      {/* Right — form */}
      <div className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <Link to="/"><img src="/images/croppedLogo.png" alt="Khoja Distributions" className="h-8 w-auto dark:brightness-0 dark:invert" /></Link>
          </div>

          <h2 className="text-3xl font-black text-foreground tracking-tight mb-1">Sign in</h2>
          <p className="text-[var(--color-muted-foreground)] text-sm mb-8">Enter your email or username to continue.</p>

          <form onSubmit={handleLogin} className="space-y-5">
            <div>
              <label className={labelCls} htmlFor="identifier">Email or Username</label>
              <input
                id="identifier"
                type="text"
                className="input-field"
                placeholder="you@business.com"
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                autoComplete="username"
                required
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className={labelCls} htmlFor="password" style={{ marginBottom: 0 }}>
                  Password
                </label>
                <Link to="/forgot" className="text-xs text-brand-amber font-semibold hover:underline">
                  Forgot password?
                </Link>
              </div>
              <input
                id="password"
                type="password"
                className="input-field"
                placeholder="Enter password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-brand-navy hover:bg-brand-amber dark:hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-white font-bold py-3.5 rounded-xl cursor-pointer"
            >
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <p className="text-sm text-center mt-6 text-[var(--color-muted-foreground)]">
            Don't have an account?{" "}
            <Link to="/register" className="text-foreground font-bold hover:text-accent transition-colors">
              Apply for Wholesale Access
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;

